import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useChildTokens } from './useChildTokens';
import { getActivityIcon } from './activityIcon';
import { NextActivityResult } from './activityStatus';

/**
 * NextUpCard — framhävt "nästa upp"-hero i barnvyn (Pipeline 3,
 * igångsättningsstöd). Medvetet INTE en ChildActivityCard: detta är ETT enskilt,
 * extra tydligt anrop till handling så barnet vet exakt vad som ska göras härnäst.
 * Stor ikon + namn + CTA, ingen status-badge (status är implicit "nu/snart").
 * Klick navigerar in i aktiviteten. Reduced-motion-säker (transform/hover slås av
 * vid prefers-reduced-motion).
 */
const NextUpCard = ({ result }: { result: NextActivityResult }) => {
  const { t } = useTranslation();
  const tokens = useChildTokens();
  const navigate = useNavigate();

  const { instance } = result;
  const icon = getActivityIcon(instance);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/activity/${instance.activityInstanceId}`)}
      sx={{
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: 'pointer',
        bgcolor: tokens.primaryLight,
        border: '2px solid',
        borderColor: 'primary.main',
        borderRadius: '16px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        transition: 'transform 0.15s',
        '@media (hover: hover)': { '&:hover': { transform: 'scale(1.01)' } },
        '&:active': { transform: 'scale(0.99)' },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
          '&:active': { transform: 'none' },
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: '0.85rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'primary.main',
        }}
      >
        👉 {t('child.nextUp.title')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box aria-hidden sx={{ fontSize: 56, lineHeight: 1, flexShrink: 0 }}>
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: '1.15rem', color: tokens.text, minWidth: 0 }}>
          {instance.activityName}
        </Typography>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'primary.main' }}>
        {t('child.nextUp.cta')} →
      </Typography>
    </Box>
  );
};

export default NextUpCard;
